import { flushSync } from 'react-dom';

/** Overlapping cross-fades keep the class until the LAST one finishes. */
let activeCrossfades = 0;

/**
 * Run `update` inside `flushSync` (committing its React state changes to the
 * DOM synchronously) and hand back its async tail, if any, for the caller to
 * await.
 */
function commitSync(update: () => void | Promise<unknown>): Promise<unknown> | undefined {
  let pending: Promise<unknown> | undefined;
  flushSync(() => {
    const result = update();
    if (result instanceof Promise) pending = result;
  });
  return pending;
}

/**
 * Run a whole-page visual switch (theme flip, language swap) inside a View
 * Transition so the entire viewport cross-fades instead of snapping.
 *
 * The `page-crossfade` class on <html> tells the consuming app's stylesheet to
 * stand down its navigation rules for the duration (see the app's
 * app/styles/index.css): the root suppression is lifted so the default
 * full-page cross-fade runs, and the named layers (`noor-content`, the nav
 * pill) drop their view-transition-names. The names MUST drop: an open overlay
 * (the mobile account popup) is flattened into the ROOT snapshot, so a content
 * region animating as its own layer would paint above the root image and
 * visually punch a hole through the popup mid-fade.
 *
 * `flushSync` commits the switch synchronously so the "new" snapshot captures
 * the changed page; an async tail (e.g. `i18n.changeLanguage`) is awaited so
 * the snapshot also waits for the re-labelled UI. Browsers without the API
 * just apply the update instantly.
 */
export function withPageCrossfade(update: () => void | Promise<unknown>): void {
  if (typeof document.startViewTransition !== 'function') {
    void update();
    return;
  }
  const root = document.documentElement;
  activeCrossfades += 1;
  root.classList.add('page-crossfade');
  const transition = document.startViewTransition(async () => {
    const pending = commitSync(update);
    if (pending) await pending;
  });
  void transition.finished.finally(() => {
    activeCrossfades -= 1;
    if (activeCrossfades === 0) root.classList.remove('page-crossfade');
  });
}
